import React, { useState, useEffect, useCallback } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import AddDocumentType from "../../../components/AddDocumentType";
import {
  Grid,
  Link,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Typography,
  Divider,
  IconButton,
  Button,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { spacing } from "@mui/system";
import Notification from "../../../components/Notification";
import ConfirmDialog from "../../../components/ConfirmDialog";
import axios from "axios";
import ViewDocumentTypeDialog from "../../../components/ViewDocumentTypeDialog";
import ViewCountryRequirementsDialog from "../../../components/ViewCountryRequirementsDialog";
import DataGridExport from "../../../components/DataGridExport";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);

const DocumentTypeDataGrid = () => {
  const columns = [
    { field: "documentTypeId", headerName: "ID", width: 50, hide: true },
    { field: "name", headerName: "Name", flex: 1 },
    { field: "shortCode", headerName: "Short Code", flex: 1 },
    { field: "description", headerName: "Description", flex: 1 },
    {
      field: "vatRequired",
      headerName: "VAT Required",
      flex: 1,
      valueGetter: (params) => (params.value ? "Yes" : "No"),
    },
    {
      field: "edRequired",
      headerName: "ED Required",
      flex: 1,
      valueGetter: (params) => (params.value ? "Yes" : "No"),
    },
    {
      field: "hasTimeRestriction",
      headerName: "Time Restricted",
      flex: 1,
      valueGetter: (params) => (params.value ? "Yes" : "No"),
    },
    {
      field: "countryRequirements",
      headerName: "Country Requirements",
      flex: 1,
      valueGetter: (params) => {
        // This column will be filled when the API is implemented
        return "View Details";
      },
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          disabled={
            !countryRequirementsCache[params.row.documentTypeId] || countryRequirementsCache[params.row.documentTypeId].length === 0
          }
          sx={{
            opacity:
              !countryRequirementsCache[params.row.documentTypeId] || countryRequirementsCache[params.row.documentTypeId].length === 0
                ? 0.6
                : 1,
            cursor:
              !countryRequirementsCache[params.row.documentTypeId] || countryRequirementsCache[params.row.documentTypeId].length === 0
                ? "not-allowed"
                : "pointer",
          }}
          onClick={async () => {
            try {
              // Gebruik de correcte endpoint: /api/v1/countryrequirements/documenttype/{documentTypeId}
              const response = await axios.get(`/api/v1/countryrequirements/documenttype/${params.row.documentTypeId}`);

              setCountryRequirementsDialog({
                isOpen: true,
                requirements: response.data || [],
                documentTypeName: params.row.name,
              });
            } catch (error) {
              console.error("Error fetching country requirements:", error);
              if (error.response && error.response.status === 404) {
                setCountryRequirementsDialog({
                  isOpen: true,
                  requirements: [],
                  documentTypeName: params.row.name,
                });
              } else {
                setNotify({
                  isOpen: true,
                  message: "Error loading country requirements",
                  type: "error",
                });
              }
            }
          }}
        >
          {countryRequirementsCache[params.row.documentTypeId] && countryRequirementsCache[params.row.documentTypeId].length > 0
            ? "View Requirements"
            : "No Requirements"}
        </Button>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => {
        return (
          <Box>
            <IconButton
              onClick={(e) => {
                setConfirmDialog({
                  isOpen: true,
                  title: "Delete Document Type",
                  subTitle: "Are you sure you want to delete " + params.row.name + "?",
                  onConfirm: () => {
                    onDeleteConfirmed(e, params.row);
                  },
                });
              }}
            >
              <DeleteIcon />
            </IconButton>
            <IconButton
              onClick={() => {
                setViewDocumentTypeDialog({
                  isOpen: true,
                  title: "View Document Type",
                  params: params.row,
                  usedInEntities: false,
                });
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const [tableData, setTableData] = useState([]);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [viewDocumentTypeDialog, setViewDocumentTypeDialog] = useState({
    isOpen: false,
    title: "",
    params: null,
    usedInEntities: false,
    onConfirm: null,
  });

  const [countryRequirementsDialog, setCountryRequirementsDialog] = useState({
    isOpen: false,
    requirements: [],
    documentTypeName: "",
  });

  const [countryRequirementsCache, setCountryRequirementsCache] = useState({});

  const fetchDocumentTypes = useCallback(async () => {
    try {
      const response = await axios.get("/api/v1/documenttypes");
      setTableData(response.data);

      // Fetch country requirements for all document types
      const requirementsPromises = response.data.map(async (docType) => {
        try {
          const reqResponse = await axios.get(`/api/v1/countryrequirements/documenttype/${docType.documentTypeId}`);
          return { [docType.documentTypeId]: reqResponse.data || [] };
        } catch (error) {
          if (error.response && error.response.status === 404) {
            return { [docType.documentTypeId]: [] };
          }
          return { [docType.documentTypeId]: [] };
        }
      });

      const requirementsResults = await Promise.all(requirementsPromises);
      const requirementsMap = requirementsResults.reduce((acc, curr) => ({ ...acc, ...curr }), {});
      setCountryRequirementsCache(requirementsMap);
    } catch (error) {
      console.error("Error fetching document types:", error);
      setNotify({
        isOpen: true,
        message: "Error fetching document types",
        type: "error",
      });
    }
  }, []);

  useEffect(() => {
    fetchDocumentTypes();
  }, [fetchDocumentTypes]);

  const onDeleteConfirmed = useCallback(
    (e, params) => {
      axios
        .delete("/api/v1/documenttypes/" + params.documentTypeId)
        .then((response) => {
          setNotify({
            isOpen: true,
            message: "Deleted Successfully",
            type: "success",
          });
          fetchDocumentTypes();
        })
        .catch(function (error) {
          if (error.response) {
            setNotify({
              isOpen: true,
              message: "Error deleting document type",
              type: "error",
            });
          }
        });

      setConfirmDialog({ ...confirmDialog, isOpen: false });
    },
    [fetchDocumentTypes, confirmDialog]
  );

  const onEditConfirmed = useCallback(
    (e, updatedData) => {
      // Convert arrays to strings for API compatibility
      const apiData = {
        ...updatedData,
        vatRequiredCountries: Array.isArray(updatedData.vatRequiredCountries)
          ? updatedData.vatRequiredCountries.join(",")
          : updatedData.vatRequiredCountries || "",
        edRequiredCountries: Array.isArray(updatedData.edRequiredCountries)
          ? updatedData.edRequiredCountries.join(",")
          : updatedData.edRequiredCountries || "",
      };

      axios
        .put("/api/v1/documenttypes/" + updatedData.documentTypeId, apiData)
        .then((response) => {
          setNotify({
            isOpen: true,
            message: "Updated Successfully",
            type: "success",
          });
          fetchDocumentTypes();
        })
        .catch(function (error) {
          if (error.response) {
            setNotify({
              isOpen: true,
              message: "Error updating document type",
              type: "error",
            });
          }
        });

      setViewDocumentTypeDialog((prev) => ({ ...prev, isOpen: false }));
    },
    [fetchDocumentTypes]
  );

  // Update cache when country requirements change
  const updateRequirementsCache = useCallback((documentTypeId, requirements) => {
    setCountryRequirementsCache((prev) => ({
      ...prev,
      [documentTypeId]: requirements || [],
    }));
  }, []);

  return (
    <React.Fragment>
      <Helmet title="Document Types" />
      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Document Types
          </Typography>
          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Link component={NavLink} to="/settings">
              Settings
            </Link>
            <Typography>Document Types</Typography>
          </Breadcrumbs>
        </Grid>
      </Grid>

      <Divider my={6} />
      <Grid item xs={12} md={6}></Grid>
      <Card mb={0}>
        <CardContent
          pb={1}
          sx={{
            marginBottom: 0,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Document Types Table
          </Typography>
          <Typography variant="body2" gutterBottom>
            In this table you can search and view Document Types. You can use the below options to customize the grid.
          </Typography>
        </CardContent>
        <Grid container spacing={0} mt={0}>
          <AddDocumentType onSuccess={fetchDocumentTypes} onRequirementsUpdate={updateRequirementsCache} />
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
          <ViewDocumentTypeDialog
            viewDocumentTypeDialog={viewDocumentTypeDialog}
            setViewDocumentTypeDialog={setViewDocumentTypeDialog}
            onRequirementsUpdate={updateRequirementsCache}
          />
          <ViewCountryRequirementsDialog
            open={countryRequirementsDialog.isOpen}
            onClose={() => setCountryRequirementsDialog({ ...countryRequirementsDialog, isOpen: false })}
            requirements={countryRequirementsDialog.requirements}
            documentTypeName={countryRequirementsDialog.documentTypeName}
          />
          <Grid item xs={12}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, mb: 2, pr: 2 }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <DataGridExport tableData={tableData} columns={columns} />
              </Box>
            </Box>
            <DataGrid
              getRowId={(row) => row.documentTypeId}
              disableSelectionOnClick
              components={{ Toolbar: GridToolbar }}
              density="compact"
              componentsProps={{
                toolbar: {
                  csvOptions: { disableToolbarButton: false },
                  printOptions: { disableToolbarButton: false },
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 250 },
                },
                rowsContainer: {
                  onClick: (e) => {
                    if (e.target.closest("button") || e.target.closest("a")) {
                      e.stopPropagation();
                    }
                  },
                },
              }}
              rows={tableData}
              columns={columns}
              pageSize={15}
              sx={{
                height: 667,
                width: "100%",
                borderRadius: 1,
                backgroundColor: "background.paper",
                boxShadow: 2,
                "& .MuiDataGrid-cell:hover": {
                  color: "primary.main",
                },
                marginTop: 0,
              }}
            />
          </Grid>
        </Grid>
      </Card>
    </React.Fragment>
  );
};

export default DocumentTypeDataGrid;
